import { type FormEvent, type ReactElement, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { HTTPError } from "ky";

import {
  type Assessment,
  createAssessment,
  deleteAssessment,
  listAssessments,
} from "@/api/assessments";
import { Btn2 } from "@/components/hifi/Btn2";
import { Card } from "@/components/hifi/Card";
import { IconBtn } from "@/components/hifi/IconBtn";
import { Pill } from "@/components/hifi/Pill";
import { ShellLayout } from "@/components/shell/ShellLayout";

type DraftAssessment = {
  title: string;
  assessment_type: string;
};

const INITIAL_DRAFT: DraftAssessment = {
  title: "",
  assessment_type: "FULL_SRA",
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function statusVariant(
  status: string,
): "ok" | "warn" | "bad" | "accent" | "default" {
  if (status === "APPROVED" || status === "COMPLETED") return "ok";
  if (status === "IN_REVIEW") return "accent";
  if (status === "REJECTED") return "bad";
  if (status === "DRAFT") return "default";
  return "warn";
}

export function AssessmentsPage(): ReactElement {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [draft, setDraft] = useState<DraftAssessment>(INITIAL_DRAFT);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      setAssessments(await listAssessments({ limit: 200 }));
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Load failed (${String(err.response.status)})`
          : "Load failed",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await createAssessment(draft);
      setDraft(INITIAL_DRAFT);
      setShowForm(false);
      await refresh();
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Create failed (${String(err.response.status)})`
          : "Create failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!window.confirm("Delete this assessment?")) return;
    try {
      await deleteAssessment(id);
      await refresh();
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Delete failed (${String(err.response.status)})`
          : "Delete failed",
      );
    }
  }

  return (
    <ShellLayout
      active="assessments"
      topbar={{
        crumbs: ["Workspace", "Assessments"],
        title: "Assessments",
        subtitle: `${String(assessments.length)} total`,
        right: (
          <Btn2
            variant="accent"
            size="sm"
            icon="plus"
            onClick={() => {
              setShowForm((v) => !v);
            }}
          >
            {showForm ? "Cancel" : "New assessment"}
          </Btn2>
        ),
      }}
    >
      <div className="space-y-5 p-5">
        {error && (
          <div
            role="alert"
            className="rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
          >
            {error}
          </div>
        )}

        {showForm && (
          <Card>
            <div className="border-b border-n-150 px-4 py-2.5">
              <div className="text-[13px] font-semibold text-n-900">
                New assessment
              </div>
            </div>
            <form
              onSubmit={(e) => {
                void handleCreate(e);
              }}
              className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4"
              noValidate
            >
              <label className="block text-[11px] font-medium text-n-600 md:col-span-2">
                Title
                <input
                  value={draft.title}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, title: e.target.value }));
                  }}
                  required
                  minLength={1}
                  maxLength={200}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                />
              </label>
              <label className="block text-[11px] font-medium text-n-600">
                Type
                <select
                  value={draft.assessment_type}
                  onChange={(e) => {
                    setDraft((d) => ({
                      ...d,
                      assessment_type: e.target.value,
                    }));
                  }}
                  className="mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
                >
                  <option value="FULL_SRA">FULL_SRA</option>
                  <option value="RAPID">RAPID</option>
                  <option value="COMPLIANCE">COMPLIANCE</option>
                </select>
              </label>
              <div className="flex items-end">
                <Btn2
                  variant="accent"
                  full
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? "Creating…" : "Create"}
                </Btn2>
              </div>
            </form>
          </Card>
        )}

        <Card>
          {loading ? (
            <p className="p-4 text-[13px] text-n-500">Loading…</p>
          ) : assessments.length === 0 ? (
            <p className="p-4 text-[13px] text-n-500">No assessments yet.</p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-n-150 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.3px] text-n-500">
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Step</th>
                  <th className="px-4 py-2">Started</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {assessments.map((a) => (
                  <tr
                    key={a.id}
                    className="cursor-pointer border-b border-n-100 last:border-0 hover:bg-n-50"
                    onClick={() => {
                      void navigate({
                        to: "/assessments/$assessmentId/wizard",
                        params: { assessmentId: a.id },
                      });
                    }}
                  >
                    <td className="px-4 py-2.5 font-medium text-n-900">
                      {a.title}
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant="outline">{a.assessment_type}</Pill>
                    </td>
                    <td className="px-4 py-2.5">
                      <Pill variant={statusVariant(a.status)}>{a.status}</Pill>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                      {a.current_step}/7
                    </td>
                    <td className="px-4 py-2.5 text-n-600">
                      {formatDate(a.started_at)}
                    </td>
                    <td
                      className="px-4 py-2 text-right"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <Btn2
                          size="xs"
                          onClick={() => {
                            void navigate({
                              to: "/assessments/$assessmentId/wizard",
                              params: { assessmentId: a.id },
                            });
                          }}
                        >
                          Open
                        </Btn2>
                        <IconBtn
                          name="x"
                          size="sm"
                          label="Delete"
                          onClick={() => {
                            void handleDelete(a.id);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </ShellLayout>
  );
}
